// Risk register view (wireframe 06) — heatmap + entity-table + Selected
// risk property grid in the main pane. UI_SPEC §3.11.
//
// Heatmap and the dashboard risk matrix both navigate here with
// `?probability=…&impact=…` query params, which filters the table. The
// Selected risk lives in the main pane only — the right pin pane is
// intentionally not used for this view (it would duplicate the same
// property grid and waste the column).

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShell } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { HeatmapWidget } from "../components/widgets/HeatmapWidget.js";
import { Card, ErrorState, FetchedSlot, Skeleton, ViewHeader } from "./common.js";

// High probability at the TOP so the orientation matches the Dashboard's
// 5×5 risk-matrix mini-widget (which puts likelihood 5 on top). Fixed Bug #6
// where the two heatmaps had opposite y-axis directions.
const PROBABILITY = ["high", "medium", "low"];
const IMPACT = ["low", "medium", "high"];
const VALID_LEVELS = new Set(PROBABILITY);

export default function RiskView() {
  const { project } = useShell();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const probFilter = searchParams.get("probability");
  const impactFilter = searchParams.get("impact");
  const filterProbability = probFilter && VALID_LEVELS.has(probFilter) ? probFilter : null;
  const filterImpact = impactFilter && VALID_LEVELS.has(impactFilter) ? impactFilter : null;
  const risks = useTool("list_risks", { project });
  // Only fetch the detail envelope when an actual risk id is selected.
  const detail = useTool("get_risk", selectedId ? { project, risk_id: selectedId } : null);

  const allRows = ((risks.env?.data as any)?.rows ?? []) as Array<any>;
  const filteredRows = useMemo(() => {
    if (!filterProbability && !filterImpact) return allRows;
    return allRows.filter((r) =>
      (!filterProbability || r.probability === filterProbability) &&
      (!filterImpact      || r.impact      === filterImpact),
    );
  }, [allRows, filterProbability, filterImpact]);

  const heatmapData = useMemo(() => {
    const cells: Array<{ row: string; col: string; value: number }> = [];
    const counts = new Map<string, number>();
    for (const r of allRows) {
      const key = `${r.probability ?? "low"}::${r.impact ?? "low"}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    for (const p of PROBABILITY) {
      for (const i of IMPACT) {
        cells.push({ row: cap(p), col: cap(i), value: counts.get(`${p}::${i}`) ?? 0 });
      }
    }
    return { rows: PROBABILITY.map(cap), columns: IMPACT.map(cap), cells };
  }, [allRows]);

  const filterActive = filterProbability !== null || filterImpact !== null;
  const clearFilter = () => setSearchParams({});

  return (
    <div className="space-y-3">
      <ViewHeader
        title={`Risk register — ${project}`}
        subtitle="Probability × impact heatmap + filterable risk list"
      />
      {filterActive && (
        <div className="flex items-center justify-between rounded border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs">
          <div className="text-sky-800">
            Filter:
            {filterProbability && <span className="ml-1.5 rounded bg-white px-1.5 py-0.5 font-medium">probability = {filterProbability}</span>}
            {filterImpact && <span className="ml-1.5 rounded bg-white px-1.5 py-0.5 font-medium">impact = {filterImpact}</span>}
            <span className="ml-2 text-sky-600">— {filteredRows.length} of {allRows.length} risks</span>
          </div>
          <button type="button" onClick={clearFilter}
                  className="rounded border border-sky-300 px-2 py-0.5 text-sky-700 hover:bg-white">
            Clear filter
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[400px_minmax(0,1fr)]">
        <Card title="Heatmap">
          <div className="h-72">
            <HeatmapWidget
              data={heatmapData}
              onCellClick={(row, col, value) => {
                if (value <= 0) return;
                const prob = row.toLowerCase();
                const impact = col.toLowerCase();
                if (!VALID_LEVELS.has(prob) || !VALID_LEVELS.has(impact)) return;
                setSearchParams({ probability: prob, impact });
              }}
            />
          </div>
        </Card>
        <Card title={`Risks (${filteredRows.length}${filterActive ? ` of ${allRows.length}` : ""})`}>
          {risks.loading ? <Skeleton /> :
           risks.error ? <ErrorState error={risks.error} onRetry={risks.refetch} /> :
           <RiskTable
             rows={filteredRows}
             selectedId={selectedId}
             onSelect={(id) => setSelectedId(id)}
           />}
        </Card>
      </div>
      <Card title="Selected risk">
        {selectedId == null
          ? <div className="p-4 text-sm text-slate-500">Click a risk above to inspect its full property grid.</div>
          : detail.loading ? <Skeleton lines={3} />
          : detail.error ? <ErrorState error={detail.error} onRetry={detail.refetch} />
          : <FetchedSlot tool="get_risk" args={{ risk_id: selectedId }} />}
      </Card>
    </div>
  );
}

function RiskTable({ rows, selectedId, onSelect }: {
  rows: any[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!rows.length) return <div className="p-4 text-sm text-slate-500">No risks.</div>;
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-slate-50 text-slate-500">
        <tr>
          <th className="px-3 py-2 text-left font-medium">ID</th>
          <th className="px-3 py-2 text-left font-medium">Title</th>
          <th className="px-3 py-2 text-left font-medium">Category</th>
          <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Prob</th>
          <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Impact</th>
          <th className="px-3 py-2 text-left font-medium">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const active = String(r.id) === selectedId;
          return (
            <tr
              key={r.id}
              className={"cursor-pointer border-t border-slate-100 " +
                         (active ? "bg-sky-50" : "hover:bg-slate-50")}
              onClick={() => onSelect(String(r.id))}
            >
              <td className="whitespace-nowrap px-3 py-1.5 font-mono text-sky-700">{r.id}</td>
              <td className="px-3 py-1.5 text-slate-800">{r.title}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-600">{r.category}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{severityChip(r.probability)}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{severityChip(r.impact)}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-600">{r.status}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function severityChip(level: string | undefined) {
  const tone = level === "high"   ? "bg-rose-100 text-rose-700"
             : level === "medium" ? "bg-amber-100 text-amber-700"
             :                      "bg-emerald-100 text-emerald-700";
  const label = level === "medium" ? "med"
              : level === "high"   ? "high"
              : level === "low"    ? "low"
              :                      "—";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${tone}`}>{label}</span>;
}
